import React from "react";
import { cn } from "../lib/utils";
import { motion } from "motion/react";
import { BranchingAnimationFull } from "./BranchingAnimationFull";
import { ScaleFreeNetwork } from "./ScaleFreeNetwork";
import { DocumentTextIcon, XMarkIcon, TrashIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { useIsMobile } from "../hooks/useIsMobile";

interface Feature {
  title: string;
  description: string;
  skeleton: React.ReactNode;
  className: string;
}

interface BentoGridProps {
  features: Feature[];
}

export function BentoGrid({ features }: BentoGridProps) {
  return (
    <div className="relative">
      <div className="grid grid-cols-1 lg:grid-cols-6 mt-12 gap-4 md:gap-6">
        {features.map((feature, index) => (
          <FeatureCard key={index} className={feature.className}>
            <FeatureTitle>{feature.title}</FeatureTitle>
            <FeatureDescription>{feature.description}</FeatureDescription>
            <div className="h-full w-full">{feature.skeleton}</div>
          </FeatureCard>
        ))}
      </div>
    </div>
  );
}

const FeatureCard = ({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={cn(
        `p-6 sm:p-8 relative overflow-hidden`,
        className
      )}
    >
      {children}
    </motion.div>
  );
};

const FeatureTitle = ({ children }: { children?: React.ReactNode }) => {
  return (
    <p className="max-w-5xl text-left tracking-tight text-white text-xl md:text-2xl md:leading-snug mb-3">
      {children}
    </p>
  );
};

const FeatureDescription = ({ children }: { children?: React.ReactNode }) => {
  return (
    <p
      className={cn(
        "text-sm md:text-base max-w-4xl text-left",
        "text-white/60 font-light",
        "mb-4"
      )}
    >
      {children}
    </p>
  );
};

// Skeleton components for different features
export const SkeletonBranching = () => {
  const isMobile = useIsMobile();

  return (
    <div className="relative overflow-visible" style={{ height: isMobile ? '780px' : '470px' }}>
      <div style={{ height: isMobile ? '1200px' : '100%' }} className="w-full transform scale-[0.65] md:scale-100 origin-top-left">
        <BranchingAnimationFull isMobile={isMobile} />
      </div>
    </div>
  );
};

// Old static version (keeping for reference)
export const SkeletonBranchingOld = () => {
  return (
    <div className="relative py-2 px-2 h-[470px]">
      <div className="w-full h-full p-3 mx-auto bg-gradient-to-br from-white/5 to-white/10 shadow-2xl rounded-lg border border-white/10">
        <div className="flex w-full h-full flex-col justify-center items-center">
          {/* Tree branching visualization */}
          <svg className="w-full h-full max-h-[430px]" viewBox="0 0 200 150" fill="none">
            <motion.circle
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ delay: 0.2 }}
              cx="100"
              cy="30"
              r="8"
              fill="#94d2bd"
              stroke="#ffffff"
              strokeWidth="2"
            />
            <motion.line
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              x1="100"
              y1="38"
              x2="100"
              y2="60"
              stroke="#5a5a5a"
              strokeWidth="2"
            />
            <motion.circle
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ delay: 0.5 }}
              cx="100"
              cy="70"
              r="6"
              fill="#005f73"
              stroke="#94d2bd"
              strokeWidth="2"
            />
            <motion.line
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              transition={{ delay: 0.6, duration: 0.4 }}
              x1="100"
              y1="76"
              x2="60"
              y2="100"
              stroke="#5a5a5a"
              strokeWidth="2"
            />
            <motion.line
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              transition={{ delay: 0.6, duration: 0.4 }}
              x1="100"
              y1="76"
              x2="140"
              y2="100"
              stroke="#5a5a5a"
              strokeWidth="2"
            />
            <motion.circle
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ delay: 0.8 }}
              cx="60"
              cy="110"
              r="6"
              fill="#005f73"
              stroke="#94d2bd"
              strokeWidth="2"
            />
            <motion.circle
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ delay: 0.8 }}
              cx="140"
              cy="110"
              r="6"
              fill="#005f73"
              stroke="#94d2bd"
              strokeWidth="2"
            />
          </svg>
        </div>
      </div>
      <div className="absolute bottom-0 z-40 inset-x-0 h-16 bg-gradient-to-t from-black via-black/50 to-transparent w-full pointer-events-none" />
    </div>
  );
};

export const SkeletonVisualFlow = () => {
  return (
    <div className="relative h-[400px]">
      <ScaleFreeNetwork />
    </div>
  );
};

export const SkeletonNotes = () => {
  return (
    <div className="relative flex flex-col h-[400px]">
      {/* Notes Component */}
      <div
        className="w-full h-full rounded-lg shadow-2xl flex flex-col"
        style={{
          backgroundColor: 'var(--bg-primary, #1a1a1a)',
          color: 'var(--text-secondary, #a0a0a0)'
        }}
      >
        {/* Header */}
        <div
          className="p-4 flex items-center justify-between"
          style={{ backgroundColor: 'var(--bg-primary, #1a1a1a)' }}
        >
          <div className="flex items-center gap-4">
            <DocumentTextIcon className="w-5 h-5" style={{ color: 'var(--accent-orange, #ee9b00)' }} />
            <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary, #ffffff)' }}>
              Tree Notes
            </h2>
          </div>

          <div className="flex items-center gap-2">
            <button
              className="p-2 rounded-lg transition-colors"
              style={{
                color: 'var(--text-secondary, #a0a0a0)',
                backgroundColor: 'transparent'
              }}
            >
              <DocumentTextIcon className="h-4 w-4" />
            </button>
            <button
              className="p-2 rounded-lg transition-colors"
              style={{
                color: 'var(--text-secondary, #a0a0a0)',
                backgroundColor: 'transparent'
              }}
            >
              <ClipboardDocumentCheckIcon className="h-4 w-4" />
            </button>
            <button
              className="p-2 rounded-lg transition-colors"
              style={{
                color: 'var(--text-secondary, #a0a0a0)',
                backgroundColor: 'transparent'
              }}
            >
              <TrashIcon className="h-4 w-4" />
            </button>
            <button
              className="p-2 rounded-lg transition-colors"
              style={{
                color: 'var(--text-secondary, #a0a0a0)',
                backgroundColor: 'transparent'
              }}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden p-4 flex flex-col">
          {/* Title Input */}
          <motion.input
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            type="text"
            value="Climate Change Research"
            readOnly
            className="w-full p-2 mb-4 rounded-lg border bg-transparent outline-none"
            style={{
              borderColor: 'var(--border-primary, rgba(255,255,255,0.1))',
              color: 'var(--text-primary, #ffffff)'
            }}
          />

          {/* Content Area - Preview Mode with actual text */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex-1 overflow-y-auto"
            style={{
              fontSize: '14px',
              lineHeight: '1.6',
              color: 'var(--text-secondary, #a0a0a0)'
            }}
          >
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary, #ffffff)' }}>
                  Key Findings
                </h3>
                <p className="mb-2">
                  Climate change mitigation requires a multi-faceted approach combining technological innovation with policy reform. The most promising solutions include:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Renewable energy transition (solar, wind, geothermal)</li>
                  <li>Carbon capture and storage technologies</li>
                  <li>Sustainable agriculture and land use practices</li>
                  <li>Electric vehicle adoption and transportation reform</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary, #ffffff)' }}>
                  Carbon Budget Model
                </h3>
                <p className="mb-3">
                  The remaining carbon budget for a 50% chance of limiting warming to 1.5°C:
                </p>
                <div className="bg-white/5 rounded-lg p-3 mb-2 overflow-x-auto">
                  <div className="text-center" style={{ color: 'var(--text-primary, #ffffff)', fontSize: '15px' }}>
                    <span className="inline-block">ΔT = λ · RF</span>
                  </div>
                  <div className="text-xs text-center mt-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    where λ ≈ 0.8 K/(W/m²) and RF = 5.35 · ln(C/C₀)
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary, #ffffff)' }}>
                  Energy Balance Equation
                </h3>
                <p className="mb-3">
                  The Earth's energy balance must satisfy:
                </p>
                <div className="bg-white/5 rounded-lg p-3 mb-2 overflow-x-auto">
                  <div className="text-center" style={{ color: 'var(--text-primary, #ffffff)', fontSize: '15px' }}>
                    <span className="inline-block">(1 - α) · S₀/4 = εσT⁴</span>
                  </div>
                  <div className="text-xs text-center mt-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    Solar input = Thermal radiation output
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary, #ffffff)' }}>
                  Research Questions
                </h3>
                <p className="mb-2">
                  What are the most cost-effective pathways to net-zero emissions by 2050? How can we balance economic development with environmental sustainability?
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary, #ffffff)' }}>
                  Important Links
                </h3>
                <p>
                  <span style={{ color: 'var(--accent-orange, #ee9b00)' }}>[[Renewable Energy Solutions]]</span>
                  {' • '}
                  <span style={{ color: 'var(--accent-orange, #ee9b00)' }}>[[Carbon Capture Methods]]</span>
                </p>
              </div>

              <div>
                <p className="italic" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  "The next decade will be critical for establishing the infrastructure and policies needed to meet our climate goals."
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <div
          className="p-3 flex items-center justify-between text-xs"
          style={{
            backgroundColor: 'var(--bg-primary, #1a1a1a)',
            color: 'var(--text-muted, rgba(255,255,255,0.4))'
          }}
        >
          <div>Last saved: Just now</div>
          <div style={{ color: 'rgba(255,255,255,0.3)' }}>
            Supports Markdown • Paste images • Auto-saves
          </div>
        </div>
      </div>

      {/* Fade out effect at bottom */}
      <div className="absolute bottom-0 z-40 inset-x-0 h-24 bg-gradient-to-t from-black via-black/70 to-transparent w-full pointer-events-none" />
    </div>
  );
};

export const SkeletonCollaboration = () => {
  const avatars = [
    { color: "from-blue-500/30 to-purple-500/30", initial: "A" },
    { color: "from-pink-500/30 to-orange-500/30", initial: "B" },
    { color: "from-green-500/30 to-teal-500/30", initial: "C" },
  ];

  return (
    <div className="relative flex flex-col items-center justify-center p-8 gap-6 h-[240px]">
      <div className="flex -space-x-4">
        {avatars.map((avatar, idx) => (
          <motion.div
            key={idx}
            initial={{ scale: 0, x: 0 }}
            whileInView={{ scale: 1, x: 0 }}
            transition={{ delay: 0.2 + idx * 0.1, type: "spring" }}
            className={`w-16 h-16 rounded-full bg-gradient-to-br ${avatar.color} border-2 border-black flex items-center justify-center`}
          >
            <span className="text-white text-xl font-semibold">
              {avatar.initial}
            </span>
          </motion.div>
        ))}
      </div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="text-center"
      >
        <div className="text-white/40 text-sm">Working together in real-time</div>
      </motion.div>
    </div>
  );
};

export const SkeletonNeverLoseTrack = () => {
  return (
    <div className="relative flex py-4 px-2 gap-4 h-full min-h-[200px]">
      <div className="w-full p-4 mx-auto bg-gradient-to-br from-white/5 to-white/10 shadow-2xl h-full rounded-lg border border-white/10">
        <div className="flex flex-1 w-full h-full flex-col justify-center items-center space-y-4">
          {/* Saved paths visualization - like a folder/archive icon */}
          <motion.div
            initial={{ scale: 0, rotate: -10 }}
            whileInView={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="relative"
          >
            <svg className="w-24 h-24" viewBox="0 0 100 100" fill="none">
              {/* Folder */}
              <motion.path
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                d="M 10 30 L 10 80 L 90 80 L 90 30 Z"
                stroke="#94d2bd"
                strokeWidth="2"
                fill="rgba(148, 210, 189, 0.1)"
              />
              <motion.path
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                d="M 10 30 L 40 30 L 45 20 L 90 20 L 90 30"
                stroke="#94d2bd"
                strokeWidth="2"
                fill="rgba(148, 210, 189, 0.2)"
              />
              {/* Check mark */}
              <motion.path
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                d="M 35 55 L 45 65 L 65 40"
                stroke="#ffffff"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="text-white/40 text-xs text-center"
          >
            All conversations saved
          </motion.div>
        </div>
      </div>
      <div className="absolute bottom-0 z-40 inset-x-0 h-16 bg-gradient-to-t from-black via-black/50 to-transparent w-full pointer-events-none" />
    </div>
  );
};

export const SkeletonKnowledgeManagement = () => {
  return (
    <div className="relative py-4 px-2 gap-4 h-[240px]">
      <div className="w-full h-full p-4 mx-auto bg-gradient-to-br from-white/5 to-white/10 shadow-2xl rounded-lg border border-white/10">
        <div className="flex w-full h-full flex-col justify-center items-center space-y-4">
          {/* Knowledge graph visualization */}
          <svg className="w-full h-full max-h-[200px]" viewBox="0 0 200 150" fill="none">
            {/* Central hub */}
            <motion.circle
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              cx="100"
              cy="75"
              r="15"
              fill="#ee9b00"
              stroke="#ffffff"
              strokeWidth="2"
            />
            {/* Surrounding nodes */}
            {[
              { x: 50, y: 40, delay: 0.3 },
              { x: 150, y: 40, delay: 0.4 },
              { x: 50, y: 110, delay: 0.5 },
              { x: 150, y: 110, delay: 0.6 },
              { x: 30, y: 75, delay: 0.7 },
              { x: 170, y: 75, delay: 0.8 },
            ].map((node, i) => (
              <g key={i}>
                <motion.line
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ delay: node.delay, duration: 0.4 }}
                  x1="100"
                  y1="75"
                  x2={node.x}
                  y2={node.y}
                  stroke="#5a5a5a"
                  strokeWidth="1.5"
                />
                <motion.circle
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ delay: node.delay + 0.2, type: "spring" }}
                  cx={node.x}
                  cy={node.y}
                  r="8"
                  fill="#005f73"
                  stroke="#94d2bd"
                  strokeWidth="1.5"
                />
              </g>
            ))}
          </svg>
        </div>
      </div>
      <div className="absolute bottom-0 z-40 inset-x-0 h-16 bg-gradient-to-t from-black via-black/50 to-transparent w-full pointer-events-none" />
    </div>
  );
};
